// UC-09: Generic column-definition driven table extracted from OrderLinesPage.
// Uses CSS custom property tokens only — no hardcoded hex values.
import React, { useState } from 'react';

export interface ColumnDef<T> {
  header: string;
  accessor: (row: T) => React.ReactNode;
  className?: string;
  /** Preferred width in pixels, including cell padding. Tables scroll below the total. */
  width?: number;
  // Identifies this column for sorting (must match a key the caller's
  // onSortChange/sort.key understands, e.g. a real backend sort field).
  // Columns without one render as a plain, non-interactive header.
  sortKey?: string;
  sticky?: boolean;
}

export interface SortState {
  key: string;
  direction: 'asc' | 'desc';
}

export interface TableProps<T> {
  columns: ColumnDef<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  emptyMessage?: string;
  onRowClick?: (row: T) => void;
  getRowClassName?: (row: T) => string;
  sort?: SortState | undefined;
  onSortChange?: (key: string) => void;
  workspace?: boolean;
}

export function Table<T>({
  columns,
  rows,
  getRowKey,
  emptyMessage = 'No records found.',
  onRowClick,
  getRowClassName,
  sort,
  onSortChange,
  workspace = false,
}: TableProps<T>): React.ReactElement {
  const [compact, setCompact] = useState(false);
  const columnWidths = columns.map((column) => column.width ?? 144);
  const minimumWidth = columnWidths.reduce((total, width) => total + width, 0);
  return (
    <>
      {workspace && rows.length > 0 && (
        <div className="flex flex-wrap items-center justify-end gap-2 border-b border-border px-4 py-1 text-micro text-muted-foreground">
          <button
            type="button"
            className="rounded-field px-2 py-1 text-primary"
            aria-pressed={compact}
            onClick={() => setCompact((value) => !value)}
          >
            {compact ? 'Comfortable density' : 'Compact density'}
          </button>
        </div>
      )}
      <div
        className={`table-scroll ${workspace ? 'max-h-[65vh] overflow-auto' : ''}`}
        tabIndex={0}
        role="region"
        aria-label="Scrollable records"
      >
        <table
          style={{ minWidth: minimumWidth }}
          className="w-full table-fixed border-collapse text-body leading-normal [&_td_button]:max-w-full [&_td_button]:whitespace-normal [&_td_button]:h-auto [&_td_button]:min-h-9 [&_td_a]:whitespace-normal"
        >
          <colgroup>
            {columns.map((column, index) => (
              <col key={column.header} style={{ width: `${columnWidths[index] / minimumWidth * 100}%` }} />
            ))}
          </colgroup>
          <thead className="bg-background">
            <tr>
              {columns.map((col) => {
                const sortable = Boolean(col.sortKey && onSortChange);
                const isSorted = sortable && sort?.key === col.sortKey;
                return (
                  <th
                    key={col.header}
                    scope="col"
                    aria-sort={
                      isSorted
                        ? sort?.direction === 'desc'
                          ? 'descending'
                          : 'ascending'
                        : undefined
                    }
                    className={`text-left font-semibold text-muted-700 text-label leading-tight break-words px-3 py-3 border-b border-border ${workspace ? 'sticky top-0 z-10 bg-background' : ''} ${col.sticky && workspace ? 'left-0 z-20' : ''} ${col.className ?? ''}`}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        onClick={() => onSortChange?.(col.sortKey as string)}
                        className="inline-flex max-w-full items-center gap-1 text-left hover:text-foreground"
                      >
                        {col.header}
                        <span className="shrink-0 text-micro" aria-hidden="true">
                          {isSorted ? (sort?.direction === 'desc' ? '▼' : '▲') : '↕'}
                        </span>
                      </button>
                    ) : (
                      col.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-12 text-center text-muted-foreground"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={getRowKey(row)}
                  onClick={(event) => {
                    if (
                      (event.target as HTMLElement).closest(
                        'button, a, input, select, textarea, summary'
                      )
                    )
                      return;
                    onRowClick?.(row);
                  }}
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={(event) => {
                    if (event.target !== event.currentTarget || !onRowClick) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onRowClick(row);
                    }
                  }}
                  className={[
                    'border-b border-neutral-150 last:border-b-0 transition-colors hover:bg-primary-100 focus-visible:bg-primary-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring',
                    onRowClick ? 'cursor-pointer' : '',
                    getRowClassName?.(row) ?? '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {columns.map((col) => (
                    <td
                      key={col.header}
                      className={`px-3 ${compact ? 'py-2' : 'py-3.5'} align-top break-words [overflow-wrap:anywhere] text-foreground ${col.sticky && workspace ? 'sticky left-0 z-[1] bg-card' : ''} ${col.className ?? ''}`}
                    >
                      <div className="table-cell-content">{col.accessor(row)}</div>
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
